import { Fragment, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XCircleIcon } from '@heroicons/react/20/solid';
import { Tooltip, Zoom } from '@mui/material';

/** `inputId`: enlaza `<label htmlFor>` con el input (accesibilidad). Si hay `label` y omites `inputId`, se usa `combobox-${property}`. */
export const ComboBox = ({
    filterData,
    query,
    setQuery,
    selected,
    setSelected,
    placeholder = '',
    property,
    inputId,
    label,
    required = false,
    error = null,
}) => {
    const [touched, setTouched] = useState(false);
    const resolvedInputId = inputId ?? (property != null && property !== '' ? `combobox-${String(property)}` : undefined);

    const handleChange = (value) => {
        setSelected((prev) => ({ ...prev, [property]: value }));
    };

    const clearSelection = () => {
        setSelected((prev) => ({ ...prev, [property]: '' }));
        setQuery('');
    };

    const rawValue = selected?.[property];
    const hasSelection = !!rawValue;
    const hasValidItem =
        rawValue != null &&
        rawValue !== '' &&
        (typeof rawValue !== 'object'
            ? String(rawValue).trim() !== ''
            : Boolean(rawValue.description ?? rawValue.value ?? rawValue.id));
    const internalError =
        required && touched && !hasValidItem && !error ? 'Este campo es requerido' : null;
    const displayError = error || internalError;

    return (
        <div className="w-full">
            {label != null && label !== '' && (
                <label htmlFor={resolvedInputId} className="block text-sm font-semibold text-gray-700 leading-6 ml-1 mb-0">
                    {label}
                    {required && (
                        <span className="text-red-500 ml-1" aria-hidden="true">
                            *
                        </span>
                    )}
                </label>
            )}
            <Combobox value={selected?.[property]} onChange={(value) => handleChange(value)}>
                <div className={`relative ${label != null && label !== '' ? 'mt-2' : 'mt-1'}`}>
                    <div className="relative w-full">
                        <Combobox.Input
                            id={resolvedInputId}
                            name={resolvedInputId != null ? String(resolvedInputId) : undefined}
                            type="text"
                            inputMode="text"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            data-lpignore="true"
                            className={`w-full rounded-xl border-0 py-2.5 px-4 text-gray-900 capitalize sm:text-sm sm:leading-6 outline-none transition-all duration-200 ${
                                displayError
                                    ? 'ring-2 ring-inset ring-red-500 bg-red-50 placeholder:text-red-400 text-red-900 focus:ring-2 focus:ring-inset focus:ring-red-500'
                                    : 'shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 focus:bg-indigo-50'
                            }`}
                            displayValue={(item) => item?.description || ''}
                            onChange={(event) => setQuery(event.target.value)}
                            onBlur={() => setTouched(true)}
                            placeholder={placeholder}
                            aria-required={required}
                            aria-invalid={displayError ? true : undefined}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-2">
                            {hasSelection && (
                                <Tooltip title="Eliminar selecion" TransitionComponent={Zoom} placement='top'>
                                    <button
                                        type="button"
                                        onClick={clearSelection}
                                        className="focus:outline-none"
                                    >
                                        <XCircleIcon
                                            className="h-5 w-5 text-gray-300 hover:text-red-500"
                                            aria-hidden="true"
                                        />
                                    </button>
                                </Tooltip>
                            )}
                            <Combobox.Button className="flex items-center">
                                <ChevronUpDownIcon
                                    className="h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                />
                            </Combobox.Button>
                        </div>
                    </div>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        afterLeave={() => setQuery('')}
                    >
                        <Combobox.Options className="absolute z-[200] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 sm:text-sm">
                            {filterData.length === 0 && query !== '' ? (
                                <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                    No se encontró.
                                </div>
                            ) : (
                                filterData.map((item, index) => (
                                    <Combobox.Option
                                        key={`${item.description}-${index}`}
                                        className={({ active }) =>
                                            `relative cursor-default select-none py-2 pl-10 pr-4 uppercase ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                        }
                                        value={item}
                                    >
                                        {({ selected, active }) => (
                                            <>
                                                <span className={`block truncate capitalize`}>
                                                    {item.description}
                                                </span>
                                                {selected && (
                                                    <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}>
                                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </Combobox.Option>
                                ))
                            )}
                        </Combobox.Options>
                    </Transition>
                </div>
            </Combobox>
            {displayError && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1 ml-1">
                    <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {displayError}
                </p>
            )}
        </div>
    );
};
